import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Send } from "lucide-react";

const EMPTY_FORM = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  message: "",
  website: "", // honeypot
};

const GENERIC_ERROR = "Une erreur est survenue lors de l'envoi de votre demande.";

export default function ContactForm({ compact = false, testIdPrefix = "contact" }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // second garde-fou contre les doubles envois
    setLoading(true);
    try {
      const { data } = await api.post("/contact", {
        ...form,
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      if (data?.success) {
        toast.success(data.message || "Message envoyé. Nous reviendrons vers vous rapidement.");
        setForm(EMPTY_FORM); // vidé uniquement après confirmation du succès
      } else {
        toast.error(data?.message || GENERIC_ERROR);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className={`bg-white border border-[#E2E8F0] ${compact ? "p-8" : "p-10 lg:p-14"}`}
    >
      {/* Honeypot anti-spam : hors écran et hors tabulation, donc invisible pour un
          visiteur et pour un lecteur d'écran, mais rempli par la plupart des robots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
        <label>
          Ne pas remplir ce champ
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Prénom" required>
          <input
            data-testid={`${testIdPrefix}-prenom`}
            value={form.prenom}
            onChange={update("prenom")}
            required
            className="gpi-input"
          />
        </Field>
        <Field label="Nom" required>
          <input
            data-testid={`${testIdPrefix}-nom`}
            value={form.nom}
            onChange={update("nom")}
            required
            className="gpi-input"
          />
        </Field>
        <Field label="E-mail" required>
          <input
            type="email"
            data-testid={`${testIdPrefix}-email`}
            value={form.email}
            onChange={update("email")}
            required
            className="gpi-input"
          />
        </Field>
        <Field label="Téléphone">
          <input
            type="tel"
            data-testid={`${testIdPrefix}-telephone`}
            value={form.telephone}
            onChange={update("telephone")}
            className="gpi-input"
          />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Message" required>
          <textarea
            data-testid={`${testIdPrefix}-message`}
            value={form.message}
            onChange={update("message")}
            required
            rows={6}
            className="gpi-input resize-none"
          />
        </Field>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-[#64748B] max-w-md">
          Vos données sont traitées dans le respect du RGPD pour répondre à votre demande.
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testIdPrefix}-submit`}
          className="gpi-btn-primary disabled:opacity-60"
        >
          {loading ? "Envoi en cours..." : "Envoyer mon message"}
          <Send size={14} />
        </button>
      </div>

      <p className="mt-4 text-sm text-[#64748B] text-center sm:text-right">
        ou par mail à{" "}
        <a
          href="mailto:contact@cabinetgpi-immobilier.fr"
          className="text-[#B8935A] underline hover:text-[#0F172A] transition-colors"
        >
          contact@cabinetgpi-immobilier.fr
        </a>
      </p>

      <style>{`
        .gpi-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 1px solid #E2E8F0;
          background: #F9F8F6;
          font-size: 0.95rem;
          font-family: 'Lato', sans-serif;
          color: #0F172A;
          outline: none;
          transition: all 0.25s ease;
        }
        .gpi-input:focus {
          border-color: #0F172A;
          background: white;
        }
      `}</style>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">
        {label} {required && <span className="text-[#B8935A]">*</span>}
      </span>
      {children}
    </label>
  );
}
